import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BRAND } from "@/lib/brand";
import { useContent } from "@/lib/cms";

const faqs = [
  {
    q: "What is WhatsApp Business API?",
    a: "WhatsApp Business API is Meta's official API for medium and large businesses to send and receive WhatsApp messages at scale. Unlike the regular WhatsApp Business app, the API supports automation, chatbots, multiple agents, broadcast campaigns and CRM integrations. Wappit is a Meta-approved Business Solution Provider that gets you live on the API quickly and compliantly.",
  },
  {
    q: "How long does onboarding take?",
    a: "Most Wappit customers go live within 24–48 hours. Our team handles WhatsApp Business account creation, number verification, Facebook Business Manager linking, display-name approval and the first set of message templates.",
  },
  {
    q: "Can I use my existing number?",
    a: "Yes. You can migrate any existing landline or mobile number to the WhatsApp Business API, provided it is not currently active on the regular WhatsApp or WhatsApp Business app. Our onboarding team will walk you through the migration.",
  },
  {
    q: "Do you assist with Green Tick verification?",
    a: "Absolutely. Wappit helps eligible businesses prepare and submit the Official Business Account (Green Tick) application with Meta — eligibility review, profile optimisation, application assistance and approval guidance. Final approval is at Meta's discretion; we maximise your chances.",
  },
  {
    q: "What are Meta conversation charges?",
    a: "Meta charges per 24-hour conversation window, with different rates for Marketing, Utility, Authentication and Service conversations. Rates vary by country. Wappit bills these at-cost (no markup) and we'll share an Indian rate-card during onboarding.",
  },
  {
    q: "Can I integrate my CRM?",
    a: "Yes. Wappit integrates two-way with HubSpot, Salesforce, Zoho, LeadSquared, Pipedrive, Shopify, WooCommerce, Google Sheets and 100+ tools via native integrations, Zapier, REST APIs and webhooks.",
  },
  {
    q: "Can I create chatbots without coding?",
    a: "Yes. Our drag-and-drop Flow Builder lets non-technical teams build chatbots with conditional logic, variables, API calls, AI replies and human handoff — without writing a line of code.",
  },
  {
    q: "How many agents can use one number?",
    a: "Wappit's Shared Team Inbox supports unlimited agents on a single WhatsApp number (subject to fair-usage policy). Assign chats, leave private notes, tag conversations and never miss a lead.",
  },
  {
    q: "Is there any setup fee?",
    a: "No hidden setup fee on any Wappit plan. Onboarding, number verification, template approvals and the first chatbot flow are all included. WhatsApp conversation charges are billed at-cost by Meta separately.",
  },
  {
    q: "Do you provide onboarding assistance?",
    a: `Yes. Every Wappit account gets a dedicated onboarding specialist plus access to our human support team (${BRAND.support.hours} IST, ${BRAND.trust.experience} of industry expertise). We don't believe in ticket-only support.`,
  },
  {
    q: "Can I send bulk WhatsApp campaigns?",
    a: "Yes. Use Wappit's Broadcast module to send approved marketing templates to segmented lists — with personalisation variables, scheduling and real-time delivery, read and reply analytics. All compliant with Meta's messaging policies.",
  },
  {
    q: "Is WhatsApp API suitable for travel agencies?",
    a: "Absolutely. Travel agencies use Wappit for itinerary sharing, real-time PNR & flight updates, package broadcasts, payment links and post-trip feedback — all from WhatsApp.",
  },
  {
    q: "Is WhatsApp API suitable for real estate companies?",
    a: "Yes. Real estate teams qualify enquiries via chatbots, share brochures and price lists, schedule site visits, send post-sale documentation and run nurture sequences — converting leads 3× faster on average.",
  },
  {
    q: "Can I connect payment links inside WhatsApp?",
    a: "Yes. Send Razorpay, PayU, Cashfree, Stripe or your custom payment links right inside the chat — track status via webhooks and trigger follow-ups automatically on success or failure.",
  },
  {
    q: "What support channels are available?",
    a: `Wappit support is available on phone, WhatsApp and email between ${BRAND.support.hours} IST. Growth and Enterprise plans include priority routing and a dedicated success manager.`,
  },
];

export const FAQ = () => {
  const { data } = useContent("faq", { items: [] });
  const items = (data.items && data.items.length > 0) ? data.items : faqs;
  return (
    <section id="faq" className="py-20 sm:py-28 bg-slate-50 overflow-hidden">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center">
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-emerald-600">FAQ</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Questions, answered
          </h2>
        </div>
        <Accordion type="single" collapsible className="mt-10 divide-y divide-slate-200 border-t border-b border-slate-200" data-testid="faq-accordion">
          {items.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-0">
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="text-left text-base sm:text-lg font-semibold text-slate-900 hover:no-underline py-5"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent data-testid={`faq-content-${i}`} className="text-slate-600 text-[15px] leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};
